import { useQuery } from "@apollo/client";
import { Button, Grid, TextFieldProps, Typography } from "@mui/material";
import { addMonths, isBefore } from "date-fns";
import { useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { AppPageNoMaxWidth } from "../../layouts";
import { convertStringToDate } from "../../utils/date-utils";
import { DateTimePickerComponent } from "../UserDetails/components/Playlists/components/DateTimePicker";
import { AccessLogTable } from "./AccessLogTable";
import { GET_ACCESS_NOTIFCATIONS } from "./queries";
import { AccessLogResponse, AccessRecord, FormInputs } from "./types";

const lastMonth = addMonths(new Date(), -1);

export const AccessLog = () => {
  const [startDateValue, setStartDateValue] = useState<Date | null>(lastMonth);
  const [endDateValue, setEndDateValue] = useState<Date | null>(new Date());

  const { control, handleSubmit, setValue } = useForm<FormInputs>({
    mode: "onChange",
    defaultValues: {
      startDate: lastMonth,
      endDate: new Date(),
    },
  });

  const handleChangeStart = (newValue: Date | null) => {
    if (newValue) {
      const startAsDate = new Date(newValue);
      setStartDateValue(startAsDate);
      setValue("startDate", startAsDate);
    }
  };
  const handleChangeEnd = (newValue: Date | null) => {
    if (newValue) {
      const endAsDate = new Date(newValue);
      setEndDateValue(endAsDate);
      setValue("endDate", endAsDate);
    }
  };

  const { data, error, loading, refetch } = useQuery<AccessLogResponse>(
    GET_ACCESS_NOTIFCATIONS,
    {
      fetchPolicy: "network-only",
    }
  );

  const onSubmit: SubmitHandler<FormInputs> = async () => {
    startDateValue?.setHours(0, 0, 0, 0);
    const startDateTime = startDateValue?.toISOString();

    endDateValue?.setHours(23, 59, 59, 999);
    const endDateTime = endDateValue?.toISOString();

    try {
      refetch({ startDate: startDateTime, endDate: endDateTime });
    } catch (e) {
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line no-console
      console.error(e);
    }
  };

  const isDateError =
    !endDateValue || !startDateValue
      ? undefined
      : isBefore(
          convertStringToDate(endDateValue) as Date,
          convertStringToDate(startDateValue) as Date
        );
  const endOnFieldProps: TextFieldProps = {
    error: isDateError,
    helperText: isDateError ? "End date cannot be before start date" : "",
  };

  const tableData =
    data?.accessRecords
      ?.filter((entry) => !!entry)
      ?.map((entry: AccessRecord) => {
        const viewedName = `${entry.account?.firstName} ${entry.account?.lastName}`;
        const viewedByName = entry.accessedBy;
        const date = entry.createdOn;
        return { viewedName, viewedByName, date };
      }) ?? [];

  return (
    <AppPageNoMaxWidth>
      <Grid container direction="column">
        <Grid item>
          <Typography variant="h5">Access Log</Typography>
          <br />
        </Grid>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container alignItems="center" spacing={2} sx={{ p: 4 }}>
            <Grid item>
              <Controller
                name="startDate"
                control={control}
                rules={{ required: true }}
                // TODO: Fix this the next time the file is edited.
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                render={({ field: { ref, ...rest } }) => (
                  <DateTimePickerComponent
                    dateValue={startDateValue}
                    handleChange={handleChangeStart}
                    label={"Start Date"}
                    required={true}
                    {...rest}
                  />
                )}
              />
            </Grid>
            <Grid item>
              <Controller
                name="endDate"
                control={control}
                // TODO: Fix this the next time the file is edited.
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                render={({ field: { ref, ...rest } }) => (
                  <DateTimePickerComponent
                    dateValue={endDateValue}
                    handleChange={handleChangeEnd}
                    label={"End Date"}
                    required={true}
                    textFieldProps={endOnFieldProps}
                    {...rest}
                  />
                )}
              />
            </Grid>
            <Grid item>
              <Button className="button" type="submit">
                Filter Results
              </Button>
            </Grid>
          </Grid>
        </form>
        <Grid item>
          <AccessLogTable
            tableData={tableData}
            loading={loading}
            error={error}
          />
        </Grid>
      </Grid>
    </AppPageNoMaxWidth>
  );
};
